import {useInfiniteQuery} from '@tanstack/react-query';
import {fetchUserPlans} from '../endpoints/api';

export interface UserEvent {
  id: number;
  title: string;
  image_url: string;
  start_date_time: string;
  end_date_time: string;
  price: number;
  is_walkabout_official: boolean;
  is_meetup: boolean;
  place?: {
    name: string;
    street_address?: string;
    city?: string;
  };
  attendeeCount: number;
  attendeeAvatars: string[];
  type: string;
  is_bookmarked: boolean;
}

const mapPlanToUserEvent = (item: any): UserEvent => ({
  id: item.id,
  title: item.title || item.name || '',
  image_url: item.image_url || item.images?.[0]?.url || '',
  start_date_time: item.start_date_time || '',
  end_date_time: item.end_date_time || '',
  price: item.price ?? 0,
  is_walkabout_official: item.is_walkabout_official ?? false,
  is_meetup: item.is_meetup ?? false,
  place: item.place
    ? {
        name: item.place.name || item.place.title,
        street_address: item.place.street_address,
        city: item.place.city,
      }
    : undefined,
  attendeeCount: item.bookmarks_count || 0,
  attendeeAvatars: [],
  type: item.type || 'event',
  is_bookmarked: item.is_bookmarked ?? false,
});

export const useUserPlans = (userId: string | undefined, enabled = true) => {
  const query = useInfiniteQuery(
    ['userPlans', userId],
    async ({pageParam = 1}) => {
      const response = await fetchUserPlans(Number(userId), {page: pageParam});
      return response.data;
    },
    {
      enabled: !!userId && enabled,
      //staleTime: 30_000,
      getNextPageParam: (lastPage: any) => {
        if (lastPage.current_page < lastPage.last_page) {
          return lastPage.current_page + 1;
        }
      },
    },
  );

  const plans = query.data?.pages.flatMap((page: any) =>
    (page.data || []).map(mapPlanToUserEvent),
  ) || [];

  return {...query, plans};
};

export const useSharedPlans = (userId: string | undefined, enabled = true) => {
  const query = useInfiniteQuery(
    ['userPlans', userId, 'shared'],
    async ({pageParam = 1}) => {
      const response = await fetchUserPlans(Number(userId), {common_only: true, page: pageParam});
      return response.data;
    },
    {
      enabled: !!userId && enabled,
      //staleTime: 30_000,
      getNextPageParam: (lastPage: any) => {
        if (lastPage.current_page < lastPage.last_page) {
          return lastPage.current_page + 1;
        }
      },
    },
  );

  const plans = query.data?.pages.flatMap((page: any) =>
    (page.data || []).map(mapPlanToUserEvent),
  ) || [];

  return {...query, plans};
};
